import {
  TEMPORAL_DATA_TYPES,
  TEMPORAL_NAME_PATTERN,
} from "@/features/app-studio/model/appQueryBuilderForm";
import {
  selectSemanticDimensionsFromGeneratedModel,
  type SemanticDimension,
} from "@/features/data-hub/model/semanticDimensions";
import {
  isHierarchicalOntology,
  ontologyValuePaths,
  selectSemanticOntologiesFromGeneratedModel,
  slugifyOntologyKey,
  type SemanticOntology,
} from "@/features/data-hub/model/semanticOntologies";

/**
 * How a dimension's values are picked in the tab filter bar:
 * - `ontology`  — pick from the linked ontology's values (a checklist).
 * - `dateRange` — a start/end date range (temporal dimensions have no ontology).
 * - `text`      — free-text value chips (no ontology, not temporal).
 * - `asOf`      — a single snapshot date ("the report as it stood then").
 *
 * `asOf` is never *derived* from the model — {@link buildTabFilterDimensions} offers a temporal
 * dimension as `dateRange`, because only the author knows whether a date column is a window to
 * report on or an instant to stand on. It is elected in the add menu, which is why it appears
 * here but never in the catalogue this module builds.
 */
export type TabFilterKind = "ontology" | "dateRange" | "asOf" | "text";

/**
 * A rolling window a `dateRange` filter can be set to instead of two picked dates.
 *
 * The filter stores the *preset*, never the window it currently covers, and the server re-resolves
 * it on every apply — so "last calendar week" saved today still means the previous complete
 * week a month from now, which a stored date pair would not.
 */
export type DateRangePreset =
  | "last_calendar_week"
  | "last_calendar_month"
  | "last_7_days"
  | "last_2_weeks"
  | "last_1_month";

/** The previous *complete* week or month — never any part of the one today sits in. */
export const CALENDAR_DATE_RANGE_PRESETS: readonly DateRangePreset[] = [
  "last_calendar_week",
  "last_calendar_month",
];

/** Windows counted back from today, today included as one of their days ("last 7 days" is 7). */
export const ROLLING_DATE_RANGE_PRESETS: readonly DateRangePreset[] = [
  "last_7_days",
  "last_2_weeks",
  "last_1_month",
];

export const DATE_RANGE_PRESETS: readonly DateRangePreset[] = [
  ...CALENDAR_DATE_RANGE_PRESETS,
  ...ROLLING_DATE_RANGE_PRESETS,
];

/** The "no preset — pick the two dates yourself" choice in the date-range mode selector. */
export const DATE_RANGE_CUSTOM = "custom";

/** What a date-range filter is set by: an explicit pair, or one of the rolling presets. */
export type DateRangeMode = typeof DATE_RANGE_CUSTOM | DateRangePreset;

/** An inclusive window, as the `YYYY-MM-DD` strings the rest of this module already speaks. */
export type DateRangeWindow = { startDate: string; endDate: string };

const DAY_MS = 86_400_000;

function isoUtc(timestamp: number): string {
  return new Date(timestamp).toISOString().slice(0, 10);
}

/**
 * The window a preset currently covers. The picker shows this for *every* preset the moment it
 * opens — including for a filter that has never been saved — so it cannot come from the server.
 *
 * Computed entirely in UTC, which is what makes duplicating the rule here safe: the server
 * resolves against `datetime.now(UTC).date()`, so feeding the same UTC date through the same
 * arithmetic gives the same window. Reading *local* date parts instead would disagree with the
 * filter actually applied, for the hours either side of midnight in any non-UTC timezone.
 *
 * Mirrors the resolvers of the same names in the backend's `app/utils/date_range_presets.py` —
 * change the two together.
 */
export function dateRangePresetWindow(
  preset: DateRangePreset,
  now: Date = new Date(),
): DateRangeWindow {
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();
  const day = now.getUTCDate();
  const today = Date.UTC(year, month, day);

  // Today is one of the counted days, so "last 7 days" starts 6 days back, not 7 — otherwise the
  // inclusive window would hold 8 days.
  if (preset === "last_7_days") {
    return { startDate: isoUtc(today - 6 * DAY_MS), endDate: isoUtc(today) };
  }
  if (preset === "last_2_weeks") {
    return { startDate: isoUtc(today - 13 * DAY_MS), endDate: isoUtc(today) };
  }
  if (preset === "last_1_month") {
    // The day after the same date last month, so 4 November starts on 5 October. A day the
    // previous month lacks clamps to its last one first: 31 March starts on 1 March.
    const daysInPreviousMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
    const sameDayLastMonth = Date.UTC(year, month - 1, Math.min(day, daysInPreviousMonth));
    return { startDate: isoUtc(sameDayLastMonth + DAY_MS), endDate: isoUtc(today) };
  }
  if (preset === "last_calendar_week") {
    // JS weeks start on Sunday (0); the rule is Monday-based, as Python's `weekday()` is.
    const mondayOffset = (new Date(Date.UTC(year, month, day)).getUTCDay() + 6) % 7;
    const thisMonday = Date.UTC(year, month, day - mondayOffset);
    return { startDate: isoUtc(thisMonday - 7 * DAY_MS), endDate: isoUtc(thisMonday - DAY_MS) };
  }
  // Day 0 of a month is the last day of the month before it.
  const endDate = isoUtc(Date.UTC(year, month, 0));
  return { startDate: `${endDate.slice(0, 7)}-01`, endDate };
}

export function asDateRangePreset(value: unknown): DateRangePreset | null {
  if (typeof value !== "string" || !(DATE_RANGE_PRESETS as readonly string[]).includes(value)) {
    return null;
  }
  return value as DateRangePreset;
}

/**
 * Where a filter's pick list comes from: the linked ontology (the default), or a SQL query the
 * author configured, re-run every time the app is opened. Date kinds pick dates, so they never
 * take a query.
 */
export type TabFilterOptionsSource = "ontology" | "query";

/**
 * Whether a filter kind can take its choices from a query. `asOf` picks one instant, so it has no
 * list to offer; a `dateRange` can — each choice is a named period ("Season 3") and its window.
 */
export function supportsOptionsQuery(kind: TabFilterKind): boolean {
  return kind === "ontology" || kind === "text" || kind === "dateRange";
}

/**
 * Which of a values query's columns plays which part (wire shape of the backend mapping).
 * ontology/text: `label_column` is shown, `value_column` applied. dateRange: `label_column` is
 * shown, and picking it applies the window from `start_column` to `end_column`.
 */
export type TabFilterOptionsMapping = {
  label_column: string | null;
  value_column: string | null;
  start_column: string | null;
  end_column: string | null;
};

export const EMPTY_OPTIONS_MAPPING: TabFilterOptionsMapping = {
  label_column: null,
  value_column: null,
  start_column: null,
  end_column: null,
};

/**
 * The mapping for a fresh result: every part already chosen that the result still has is kept,
 * and the rest default by column order — shown first, then applied (or start, end). Mirrors the
 * server's defaults, so what the selects show is what gets saved.
 */
export function defaultOptionsMapping(
  kind: TabFilterKind,
  columns: string[],
  previous: TabFilterOptionsMapping,
): TabFilterOptionsMapping {
  const keep = (column: string | null) =>
    column !== null && columns.includes(column) ? column : null;
  const columnAt = (index: number) => columns[index] ?? null;
  const label = keep(previous.label_column) ?? columnAt(0);
  if (kind === "dateRange") {
    return {
      label_column: label,
      value_column: null,
      start_column: keep(previous.start_column) ?? columnAt(1),
      end_column: keep(previous.end_column) ?? columnAt(2),
    };
  }
  return {
    label_column: label,
    value_column: keep(previous.value_column) ?? columnAt(1) ?? label,
    start_column: null,
    end_column: null,
  };
}

/** Whether every part the kind needs is chosen. */
export function isOptionsMappingComplete(
  kind: TabFilterKind,
  mapping: TabFilterOptionsMapping,
): boolean {
  if (mapping.label_column === null) {
    return false;
  }
  return kind === "dateRange"
    ? mapping.start_column !== null && mapping.end_column !== null
    : mapping.value_column !== null;
}

/**
 * One selectable choice: `label` is shown, `value` is what the filter applies (the warehouse code).
 * A query-backed date range's choice also carries its window (`YYYY-MM-DD`), and its `value` is
 * the key the pick is remembered by.
 */
export type TabFilterOntologyValue = {
  label: string;
  value: string;
  start_date?: string | null;
  end_date?: string | null;
};

/**
 * A persisted filter value (the builder's app default, or a viewer's per-user preference). Wire
 * shape mirrors the backend `AppTabFilterValue`: `values` for ontology/text, `start_date`/`end_date`
 * for a date range. `null` fields mean "not set".
 */
export type TabFilterValue = {
  values: string[];
  start_date: string | null;
  end_date: string | null;
  /** A rolling window, set *instead of* `start_date`/`end_date`. */
  date_preset: DateRangePreset | null;
  /**
   * A special choice, set instead of picking values: the filter's first value (an author's
   * default, re-picked on every open), all of its values, or no filter at all. Read back from the
   * server with `values` / dates already resolved to what it means now.
   */
  special?: TabFilterSpecial | null;
};

/** The special choices a filter with a list of values can be set to. */
export type TabFilterSpecial = "first" | "all" | "none";

export function asTabFilterSpecial(value: unknown): TabFilterSpecial | null {
  return value === "first" || value === "all" || value === "none" ? value : null;
}

/** What an empty selection does: leave the filter out, or apply it so it matches no rows. */
export type TabFilterEmptyBehavior = "skip" | "match_nothing";

/** What a nested filter's selection becomes when a filter its values query reads changes. */
export type TabFilterParentChangeRule = "keep_or_first" | "first" | "all" | "clear";

/**
 * How a filter's selection behaves (wire shape of the backend `SelectionConfig`):
 * - `all_*` / `none_*` — "All" / "No filter" offered to viewers, and their labels (`null` label =
 *   the default wording);
 * - `multi` — several values or one (`null` = the kind's default: several, except a list of
 *   named periods, which applies one window);
 * - `empty_behavior` — what an empty selection does;
 * - `on_parent_change` — for a nested filter, what its selection becomes when its parent changes.
 */
export type TabFilterSelectionConfig = {
  all_enabled: boolean;
  all_label: string | null;
  none_enabled: boolean;
  none_label: string | null;
  multi: boolean | null;
  empty_behavior: TabFilterEmptyBehavior;
  on_parent_change: TabFilterParentChangeRule;
};

export const DEFAULT_TAB_FILTER_SELECTION: TabFilterSelectionConfig = {
  all_enabled: false,
  all_label: null,
  none_enabled: false,
  none_label: null,
  multi: null,
  empty_behavior: "skip",
  on_parent_change: "keep_or_first",
};

/** A dimension the tab filter bar can filter on, with its resolved value-picker kind. */
/** Which level of which multi-column ontology a filter's options come from. */
export type TabFilterOntologyLevel = {
  ontologyKey: string;
  level: number;
};

export type TabFilterDimension = {
  /** Stable identity used as the option value: the dimension's `key`, or `name` when it has none. */
  value: string;
  /** Display label. */
  name: string;
  /** Whether `value` is the dimension's `key` or its `name`. */
  by: "key" | "name";
  filterKind: TabFilterKind;
  /**
   * The pick list: populated when `filterKind === "ontology"`, or for any kind whose values come
   * from a query (`optionsSource === "query"`). `null` means "no list — type values freely".
   */
  ontologyValues: TabFilterOntologyValue[] | null;
  /** Absent for a dimension the filter bar built from the model rather than a saved field. */
  optionsSource?: TabFilterOptionsSource;
  /** The configured values query when `optionsSource === "query"`. */
  optionsSql?: string | null;
  /** How that query's columns map onto shown / applied (or a period's start / end). */
  optionsMapping?: TabFilterOptionsMapping | null;
  /** Why the last run of the values query produced no options, if it failed. */
  optionsError?: string | null;
  /** The dimension's own name, which resetting a renamed filter restores. */
  defaultName?: string;
  /** Whether `name` is an author's label rather than the dimension's own name. */
  hasCustomName?: boolean;
  /** How the selection behaves (absent = every default). */
  selectionConfig?: TabFilterSelectionConfig;
  /**
   * The saved default — what the app opens with. Kept apart from the selection on screen, which
   * the author changes freely while building and which is never saved as the default.
   */
  defaultValue?: TabFilterValue | null;
  /**
   * The other filters this one's values query reads (`{{filter: column}}`), as written — a key,
   * a dimension name or a chip label. When one of them changes, this filter's list is re-run.
   */
  parents?: string[];
  /**
   * The level of a multi-column ontology this filter's options come from. Every filter on the
   * same ontology narrows the others, so a change to one re-evaluates them all.
   */
  ontologyLevel?: TabFilterOntologyLevel | null;
  /** Shown on every tab of the app rather than one (absent = this tab's own). */
  appliesToAllTabs?: boolean;
  /** Shown on exactly these tabs (two or more), or absent / `null` when not shared that way. */
  sharedTabIds?: number[] | null;
};

/**
 * Where a filter is shown: this tab only, every tab (tabs added later included), or the selected
 * tabs. A shared filter is one saved row, so its default and a viewer's pick carry across every
 * tab that shows it.
 */
export type TabFilterScope =
  | { kind: "tab" }
  | { kind: "allTabs" }
  | { kind: "selectedTabs"; tabIds: number[] };

/** The scope a filter is saved with. */
export function filterScopeOf(dimension: {
  appliesToAllTabs?: boolean;
  sharedTabIds?: number[] | null;
}): TabFilterScope {
  if (dimension.appliesToAllTabs === true) {
    return { kind: "allTabs" };
  }
  if (dimension.sharedTabIds !== undefined && dimension.sharedTabIds !== null) {
    return { kind: "selectedTabs", tabIds: dimension.sharedTabIds };
  }
  return { kind: "tab" };
}

/** Whether a filter is shown on more than the one tab: every tab, or a selection of them. */
export function isSharedDimension(dimension: {
  appliesToAllTabs?: boolean;
  sharedTabIds?: number[] | null;
}): boolean {
  return filterScopeOf(dimension).kind !== "tab";
}

/**
 * Whether `outer` shows a filter on every tab `inner` does, given the tab `inner` is set from.
 * Only "every tab" covers "every tab": a selection listing every tab today stops covering it
 * once a tab is added. Mirrors `FilterScope.covers` in the backend's `tab_filter_scope.py`.
 */
export function scopeCovers(
  outer: TabFilterScope,
  inner: TabFilterScope,
  currentTabId: number,
): boolean {
  if (outer.kind === "allTabs") {
    return true;
  }
  if (inner.kind === "allTabs") {
    return false;
  }
  const outerTabs = outer.kind === "selectedTabs" ? outer.tabIds : [currentTabId];
  const innerTabs = inner.kind === "selectedTabs" ? inner.tabIds : [currentTabId];
  return innerTabs.every((tabId) => outerTabs.includes(tabId));
}

/**
 * An active, in-memory filter for one dimension on one tab. Discriminated by `kind` so Part 2
 * can translate each entry to a `WHERE` predicate (`IN (...)` for ontology/text, `BETWEEN` for
 * a date range).
 */
export type TabFilterSelection =
  | {
      kind: "ontology" | "text";
      dimValue: string;
      dimName: string;
      by: "key" | "name";
      values: string[];
      /** A special choice; `values` then holds what it resolves to (empty for "none"). */
      special?: TabFilterSpecial | null;
      /** Apply the filter, matching no rows, when nothing is picked (author's setting). */
      matchNothingWhenEmpty?: boolean;
      /**
       * The values came from a values query, so they are already warehouse codes: the server
       * matches them as given instead of translating them through the semantic model.
       */
      rawValues?: boolean;
    }
  | {
      kind: "asOf";
      dimValue: string;
      dimName: string;
      by: "key" | "name";
      /**
       * The snapshot date, `YYYY-MM-DD`, or `""` before one is picked. One field rather than a
       * window: the server mirrors it into both ends so every range-shaped consumer downstream
       * sees a well-formed one-day window, but nothing in the UI should offer a second date to
       * pick — there is only ever one.
       */
      asOfDate: string;
    }
  | {
      kind: "dateRange";
      dimValue: string;
      dimName: string;
      by: "key" | "name";
      /**
       * The window in effect, always populated — hand-picked when `datePreset` is null, otherwise
       * derived from it. Keeping them filled either way is what lets one pair of date fields serve
       * both modes, and what stops the displayed dates jumping when the mode changes.
       */
      startDate: string;
      endDate: string;
      /**
       * When set, the window is rolling: `startDate`/`endDate` are a *view* of it, re-derived on
       * every load, and only the preset is persisted or sent.
       */
      datePreset: DateRangePreset | null;
      /**
       * A query-backed date range's picked key ("Season 3"). The window it names is what
       * applies; the key is what the picker shows as chosen, and is re-read against the query's
       * fresh choices on every load so the dates follow the warehouse.
       */
      optionValue?: string | null;
      /** A special choice on a list of periods; the window then holds what it resolves to. */
      special?: TabFilterSpecial | null;
      /** Apply the filter, matching no rows, when no window is set (author's setting). */
      matchNothingWhenEmpty?: boolean;
    };

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" ? (value as Record<string, unknown>) : {};
}

function normalizeFieldName(value: string): string {
  return value.trim().toLowerCase();
}

/** Last path segment of a qualified column ref, e.g. `customers.region` -> `region`. */
function lastSegment(value: string): string {
  return value.includes(".") ? (value.split(".").pop() ?? value) : value;
}

/** The physical column a dimension binds to, from `source_ref.column` or its `expression`. */
function dimensionColumn(dimension: SemanticDimension): string {
  const sourceRef = asRecord(dimension.source_ref);
  const column = typeof sourceRef.column === "string" ? sourceRef.column : "";
  if (column) {
    return column;
  }
  const expression = (dimension.expression ?? "").trim();
  return expression ? lastSegment(expression) : "";
}

/** True when a dimension represents a date/time field (so it filters via a date range). */
export function isTemporalDimension(dimension: SemanticDimension): boolean {
  if ((dimension as { is_time_dimension?: unknown }).is_time_dimension === true) {
    return true;
  }
  const sourceRef = asRecord(dimension.source_ref);
  const dataType = typeof sourceRef.data_type === "string" ? sourceRef.data_type.toLowerCase() : "";
  if (dataType && TEMPORAL_DATA_TYPES.has(dataType)) {
    return true;
  }
  const candidates = [dimension.name ?? "", dimension.key ?? "", dimensionColumn(dimension)];
  return candidates.some((candidate) => candidate !== "" && TEMPORAL_NAME_PATTERN.test(candidate));
}

/** Whether a normalized field names `sourceField`: its full path or its bare column. */
function fieldMatchesSource(normalizedField: string, rawSourceField: string): boolean {
  const sourceField = normalizeFieldName(rawSourceField);
  if (!sourceField) {
    return false;
  }
  const sourceColumn = lastSegment(sourceField);
  // Exact matches only (mirrors the backend). A substring match — e.g. the source column
  // "channel" being contained in "derived_channel_name" — would link a filter to the wrong
  // ontology and surface the wrong value set, so columns are compared segment-to-segment instead.
  return (
    normalizedField === sourceField ||
    normalizedField === sourceColumn ||
    (sourceColumn !== "" && lastSegment(normalizedField) === sourceColumn)
  );
}

/** The index of the multi-column ontology's level a normalized field names (key, name, column). */
function levelForField(normalizedField: string, ontology: SemanticOntology): number | null {
  const levels = ontology.levels ?? [];
  for (let index = 0; index < levels.length; index += 1) {
    const level = levels[index];
    if (
      normalizedField === normalizeFieldName(level.key ?? "") ||
      normalizedField === normalizeFieldName(level.name ?? "") ||
      fieldMatchesSource(normalizedField, level.source_field ?? "")
    ) {
      return index;
    }
  }
  return null;
}

/**
 * Whether a single normalized field string identifies a flat ontology. Mirrors the backend's
 * `_field_matches_ontology` (app/sql/model/ontologies.py): match on the ontology's name/key,
 * its `source_field`, or the column within it.
 */
function fieldMatchesOntology(normalizedField: string, ontology: SemanticOntology): boolean {
  if (!normalizedField) {
    return false;
  }
  if (
    normalizedField === normalizeFieldName(ontology.name ?? "") ||
    normalizedField === normalizeFieldName(ontology.key ?? "")
  ) {
    return true;
  }
  return fieldMatchesSource(normalizedField, ontology.source_field ?? "");
}

/** A dimension's link to an ontology, with the level for a multi-column one (else `null`). */
export type OntologyLink = { ontology: SemanticOntology; level: number | null };

/**
 * Find the ontology whose values a dimension can take. There is no foreign key between them;
 * the link is resolved by string, so we test each of the dimension's identifiers (its column,
 * key, and name) against every ontology using the same matcher the backend uses at query time.
 *
 * A multi-column ontology is matched through its levels and wins over a flat one on the same
 * column, as on the backend (`link_ontology_level_for_field`).
 */
export function linkOntologyLevelForDimension(
  dimension: SemanticDimension,
  ontologies: SemanticOntology[],
): OntologyLink | null {
  const candidates = [dimensionColumn(dimension), dimension.key ?? "", dimension.name ?? ""]
    .map(normalizeFieldName)
    .filter((candidate) => candidate !== "");
  for (const ontology of ontologies.filter(isHierarchicalOntology)) {
    for (const candidate of candidates) {
      const level = levelForField(candidate, ontology);
      if (level !== null) {
        return { ontology, level };
      }
    }
  }
  for (const ontology of ontologies) {
    if (isHierarchicalOntology(ontology)) {
      continue;
    }
    if (candidates.some((candidate) => fieldMatchesOntology(candidate, ontology))) {
      return { ontology, level: null };
    }
  }
  return null;
}

export function linkOntologyForDimension(
  dimension: SemanticDimension,
  ontologies: SemanticOntology[],
): SemanticOntology | null {
  return linkOntologyLevelForDimension(dimension, ontologies)?.ontology ?? null;
}

/**
 * The distinct values at one level of a multi-column ontology. The value is the slug, which is
 * what the backend offers and matches (and the key a flat ontology gives the same value).
 */
function ontologyLevelOptions(
  ontology: SemanticOntology,
  level: number,
): TabFilterOntologyValue[] {
  const options: TabFilterOntologyValue[] = [];
  const seen = new Set<string>();
  for (const path of ontologyValuePaths(ontology)) {
    const label = (path[level] ?? "").trim();
    const value = slugifyOntologyKey(label) || label.toLowerCase();
    if (!label || seen.has(value)) {
      continue;
    }
    seen.add(value);
    options.push({ label, value });
  }
  return options;
}

/** The distinct, non-empty values of an ontology (label = node name; value = node key, else name).
 *
 * The value is the node's stable `key` (falling back to its display name when a key is absent).
 * The backend matches a filter value against a node's key **or** name, so sending the key is the
 * robust choice — it survives display-name changes/collisions — and the backend then resolves it to
 * the actual warehouse code (`source_ref.value`, else name) when building the SQL predicate.
 */
function ontologyValueOptions(ontology: SemanticOntology): TabFilterOntologyValue[] {
  const options: TabFilterOntologyValue[] = [];
  const seen = new Set<string>();
  for (const node of ontology.nodes ?? []) {
    const label = (node.name ?? node.key ?? "").trim();
    if (!label) {
      continue;
    }
    const key = typeof node.key === "string" ? node.key.trim() : "";
    const value = key || label;
    if (seen.has(value)) {
      continue;
    }
    seen.add(value);
    options.push({ label, value });
  }
  return options;
}

/**
 * Build the list of filterable dimensions for a domain's generated model. Offers **all**
 * dimensions; each dimension's `filterKind` is resolved in priority order: a linked ontology
 * with values -> `ontology`; else a temporal dimension -> `dateRange`; else -> `text`.
 */
export function buildTabFilterDimensions(
  generatedModel: Record<string, unknown> | null,
): TabFilterDimension[] {
  if (generatedModel === null) {
    return [];
  }
  const dimensions = selectSemanticDimensionsFromGeneratedModel(generatedModel);
  const ontologies = selectSemanticOntologiesFromGeneratedModel(generatedModel);

  const result: TabFilterDimension[] = [];
  const seen = new Set<string>();
  for (const dimension of dimensions) {
    const name = (dimension.name ?? "").trim();
    if (!name) {
      continue;
    }
    const key = (dimension.key ?? "").trim();
    const value = key || name;
    const identity = value.toLowerCase();
    if (seen.has(identity)) {
      continue;
    }
    seen.add(identity);

    const link = linkOntologyLevelForDimension(dimension, ontologies);
    const ontologyValues = link
      ? link.level !== null
        ? ontologyLevelOptions(link.ontology, link.level)
        : ontologyValueOptions(link.ontology)
      : [];

    let filterKind: TabFilterKind;
    if (ontologyValues.length > 0) {
      filterKind = "ontology";
    } else if (isTemporalDimension(dimension)) {
      filterKind = "dateRange";
    } else {
      filterKind = "text";
    }

    result.push({
      value,
      name,
      by: key ? "key" : "name",
      filterKind,
      ontologyValues: filterKind === "ontology" ? ontologyValues : null,
      ontologyLevel:
        link && link.level !== null && link.ontology.key
          ? { ontologyKey: link.ontology.key, level: link.level }
          : null,
    });
  }
  return result;
}

/**
 * A selection for a dimension, seeded from a persisted `value` when given (else empty). Used both
 * when the user adds a fresh filter (no value) and when hydrating saved default/preference values
 * on load.
 */
export function createFilterSelection(
  dimension: TabFilterDimension,
  value: TabFilterValue | null = null,
  now: Date = new Date(),
  options: { isNew?: boolean } = {},
): TabFilterSelection {
  let selection = buildSelection(dimension, value, now, options);
  // The author's "show no data when empty" and a values query's raw codes travel with the
  // selection, so every place that turns selections into a request applies them without needing
  // the filter's settings at hand.
  if (selection.kind !== "asOf" && dimension.selectionConfig?.empty_behavior === "match_nothing") {
    selection = { ...selection, matchNothingWhenEmpty: true };
  }
  if (
    (selection.kind === "ontology" || selection.kind === "text") &&
    dimension.optionsSource === "query"
  ) {
    selection = { ...selection, rawValues: true };
  }
  return selection;
}

function buildSelection(
  dimension: TabFilterDimension,
  value: TabFilterValue | null,
  now: Date,
  { isNew = false }: { isNew?: boolean },
): TabFilterSelection {
  const base = { dimValue: dimension.value, dimName: dimension.name, by: dimension.by };
  // A filter the author has just added opens on its first value, as the server stores it.
  const special = value?.special ?? (isNew && hasPickList(dimension) ? "first" : null);
  if (special !== null && hasPickList(dimension)) {
    const plain = buildSelection(dimension, null, now, {});
    // The server sends a special choice already resolved; only a value it has not seen yet
    // (a filter added a moment ago) is resolved here, against the same ordered list.
    const fromServer = value?.special
      ? (value.values.length > 0 || value.start_date !== null)
      : false;
    if (fromServer && value) {
      const resolved = buildSelection(dimension, { ...value, special: null }, now, {});
      return { ...resolved, special } as TabFilterSelection;
    }
    return withSpecial(plain, dimension, special);
  }
  if (dimension.filterKind === "asOf") {
    // Either end names the date — the server mirrors them — so a stored value is read from
    // whichever is populated rather than assuming which one was written.
    return { kind: "asOf", ...base, asOfDate: value?.start_date ?? value?.end_date ?? "" };
  }
  if (dimension.filterKind === "dateRange" && dimension.optionsSource === "query") {
    const optionValue = value?.values[0] ?? null;
    const option = (dimension.ontologyValues ?? []).find((entry) => entry.value === optionValue);
    // The query's current window for the key wins; the stored pair is only a fallback for a key
    // the query has stopped returning, so the filter still shows what it was last applied as.
    return {
      kind: "dateRange",
      ...base,
      startDate: option?.start_date ?? value?.start_date ?? "",
      endDate: option?.end_date ?? value?.end_date ?? "",
      datePreset: null,
      optionValue,
    };
  }
  if (dimension.filterKind === "dateRange") {
    const datePreset = asDateRangePreset(value?.date_preset);
    // A stored preset carries no dates of its own, so the window is derived here. Without this the
    // fields would come back empty for a saved preset, even though a window is plainly in effect.
    const window =
      datePreset !== null
        ? dateRangePresetWindow(datePreset, now)
        : { startDate: value?.start_date ?? "", endDate: value?.end_date ?? "" };
    return { kind: "dateRange", ...base, ...window, datePreset };
  }
  return {
    kind: dimension.filterKind === "ontology" ? "ontology" : "text",
    ...base,
    values: value?.values ?? [],
  };
}

/** Serialize a selection to the persisted value payload (for saving a default or preference). */
export function selectionToValuePayload(selection: TabFilterSelection): TabFilterValue {
  if (selection.kind !== "asOf" && selection.special) {
    // The choice itself is stored, never what it resolves to today, so it re-resolves on open.
    return {
      values: [],
      start_date: null,
      end_date: null,
      date_preset: null,
      special: selection.special,
    };
  }
  if (selection.kind === "asOf") {
    // Both ends, so a value round-tripped through the server comes back the shape it went out
    // as, and no consumer has to know which field an as-of filter "really" uses.
    const picked = selection.asOfDate || null;
    return { values: [], start_date: picked, end_date: picked, date_preset: null };
  }
  if (selection.kind === "dateRange") {
    // A preset and a date pair answer the same question, so only one is ever persisted. Dates the
    // user typed before switching to a preset stay in memory but are deliberately not saved:
    // stored next to the preset they would read as a window somebody had chosen.
    if (selection.datePreset !== null) {
      return { values: [], start_date: null, end_date: null, date_preset: selection.datePreset };
    }
    return {
      // The picked key travels with its window so a reload can show it as chosen.
      values: selection.optionValue ? [selection.optionValue] : [],
      start_date: selection.startDate || null,
      end_date: selection.endDate || null,
      date_preset: null,
    };
  }
  return { values: selection.values, start_date: null, end_date: null, date_preset: null };
}

/** Whether a filter offers a list of choices — what "first value", "All" and "No filter" pick from. */
export function hasPickList(dimension: TabFilterDimension): boolean {
  if (dimension.optionsSource === "query") {
    return dimension.filterKind !== "asOf";
  }
  return dimension.filterKind === "ontology" && dimension.ontologyValues !== null;
}

/**
 * A filter's choices in the order its picker shows them — which is what "first" means. A values
 * query keeps its own row order (its ORDER BY says which comes first); ontology values are listed
 * alphabetically. Mirrors `picker_order` in the backend's `tab_filter_choices.py`.
 */
export function pickerOptions(dimension: TabFilterDimension): TabFilterOntologyValue[] {
  const options = dimension.ontologyValues ?? [];
  if (dimension.optionsSource === "query") {
    return options;
  }
  return [...options].sort((first, second) => first.label.localeCompare(second.label));
}

/**
 * Set a selection to a special choice, resolving it against the filter's current list — the same
 * resolution the server applies (`expand_special`), so what the picker shows is what applies.
 * `null` clears the choice and keeps whatever values are already picked.
 */
export function withSpecial(
  selection: TabFilterSelection,
  dimension: TabFilterDimension,
  special: TabFilterSpecial | null,
): TabFilterSelection {
  if (selection.kind === "asOf") {
    return selection;
  }
  if (special === null) {
    return { ...selection, special: null };
  }
  const options = pickerOptions(dimension);
  const picked = special === "none" ? [] : special === "first" ? options.slice(0, 1) : options;
  if (selection.kind === "dateRange") {
    const starts = picked.map((option) => option.start_date).filter(Boolean) as string[];
    const ends = picked.map((option) => option.end_date).filter(Boolean) as string[];
    const hasWindow = starts.length > 0 && ends.length > 0;
    return {
      ...selection,
      special,
      datePreset: null,
      optionValue: special === "first" && hasWindow ? picked[0].value : null,
      // ISO dates compare correctly as strings.
      startDate: hasWindow ? [...starts].sort()[0] : "",
      endDate: hasWindow ? [...ends].sort()[ends.length - 1] : "",
    };
  }
  return { ...selection, special, values: picked.map((option) => option.value) };
}

/** Set a query-backed date range to one of its named periods (or clear it with `null`). */
export function withDateRangeOption(
  selection: Extract<TabFilterSelection, { kind: "dateRange" }>,
  option: TabFilterOntologyValue | null,
): Extract<TabFilterSelection, { kind: "dateRange" }> {
  if (option === null) {
    return {
      ...selection,
      optionValue: null,
      startDate: "",
      endDate: "",
      datePreset: null,
      special: null,
    };
  }
  return {
    ...selection,
    special: null,
    optionValue: option.value,
    startDate: option.start_date ?? "",
    endDate: option.end_date ?? "",
    datePreset: null,
  };
}

/** What a date-range selection is currently set by — drives the mode selector. */
export function dateRangeModeOf(
  selection: Extract<TabFilterSelection, { kind: "dateRange" }>,
): DateRangeMode {
  return selection.datePreset ?? DATE_RANGE_CUSTOM;
}

/**
 * Switch a date-range selection between an explicit pair and a rolling preset.
 *
 * Picking a preset fills the date fields with the window it names; switching to Custom keeps
 * whatever they are showing and simply unlocks them. Either way the dates on screen never jump,
 * so "start from last week and nudge it" is just two clicks and an edit.
 */
export function withDateRangeMode(
  selection: Extract<TabFilterSelection, { kind: "dateRange" }>,
  mode: DateRangeMode,
  now: Date = new Date(),
): TabFilterSelection {
  const datePreset = mode === DATE_RANGE_CUSTOM ? null : mode;
  if (datePreset === selection.datePreset) {
    return selection;
  }
  if (datePreset === null) {
    return { ...selection, datePreset: null };
  }
  return { ...selection, datePreset, ...dateRangePresetWindow(datePreset, now) };
}

/** True when a selection has enough input to be meaningful (drives the chip's summary). */
export function isSelectionComplete(selection: TabFilterSelection): boolean {
  if (selection.kind === "asOf") {
    return Boolean(selection.asOfDate);
  }
  // "No filter" is a deliberate choice, not a filter left blank — the chip shows it as set.
  if (selection.special === "none") {
    return true;
  }
  if (selection.kind === "dateRange") {
    // A preset names its own window, so it is complete without either date being picked.
    return selection.datePreset !== null || Boolean(selection.startDate && selection.endDate);
  }
  return selection.values.length > 0;
}

/**
 * Short, human-readable summary of a selection's chosen value(s) for the chip label.
 *
 * An `ontology` selection stores each option's stable key in `values` (see
 * {@link ontologyValueOptions}); map it back to the display label the value picker shows,
 * falling back to the raw value if the option is no longer present (e.g. a stale key).
 *
 * `presetLabel` is the translated name of a `dateRange` selection's preset. This module holds no
 * i18n, so the caller that has `t` passes it in; without one the raw preset key is shown.
 */
export function summarizeSelection(
  selection: TabFilterSelection,
  ontologyValues: TabFilterOntologyValue[] | null = null,
  presetLabel: string | null = null,
  specialLabels: { all: string; none: string } | null = null,
): string {
  if (selection.kind === "asOf") {
    return selection.asOfDate;
  }
  // "All" and "No filter" read as the choice, not as the list of values behind it.
  if (selection.special === "all" || selection.special === "none") {
    return specialLabels?.[selection.special] ?? selection.special;
  }
  if (selection.kind === "dateRange") {
    if (selection.datePreset !== null) {
      // Just the name here — the chip stays narrow, and the picker spells out the dates.
      return presetLabel ?? selection.datePreset;
    }
    if (selection.optionValue) {
      // A named period reads by its name ("Season 3"), as the picker shows it.
      const picked = ontologyValues?.find((option) => option.value === selection.optionValue);
      return picked?.label ?? selection.optionValue;
    }
    return `${selection.startDate} → ${selection.endDate}`;
  }
  // A query-backed text filter has a pick list too, so labels are mapped for either kind.
  if (ontologyValues) {
    const labelByValue = new Map(ontologyValues.map((option) => [option.value, option.label]));
    return selection.values.map((value) => labelByValue.get(value) ?? value).join(", ");
  }
  return selection.values.join(", ");
}

/** Wire shape sent to `POST /v1/apps/{id}/items/data` — mirrors the backend `AppTabFilter`. */
export type AppTabFilterRequest = {
  by: "key" | "name";
  value: string;
  kind: TabFilterKind;
  values?: string[];
  start_date?: string;
  end_date?: string;
  /** Sent *instead of* `start_date`/`end_date`; the server resolves it as it applies the filter. */
  date_preset?: DateRangePreset;
  /** Apply the filter so it matches no rows (an empty selection set to "show no data"). */
  match_nothing?: boolean;
  /** The values are warehouse codes from a values query: match them as given. */
  raw_values?: boolean;
};

/**
 * Map the active tab's selections to the backend request, dropping incomplete ones so a
 * half-filled filter never re-runs a widget. ontology/text carry `values`; a date range carries
 * `start_date`/`end_date`, or `date_preset` when it is set to a rolling window.
 */
export function toTabFilterRequest(selections: TabFilterSelection[]): AppTabFilterRequest[] {
  const requests: AppTabFilterRequest[] = [];
  for (const selection of selections) {
    // "No filter" applies nothing; the other choices already hold the values they resolve to.
    if (selection.kind !== "asOf" && selection.special === "none") {
      continue;
    }
    if (!isSelectionComplete(selection)) {
      // Empty: left out, unless the author set this filter to show no data when empty.
      if (selection.kind !== "asOf" && selection.matchNothingWhenEmpty) {
        requests.push({
          by: selection.by,
          value: selection.dimValue,
          kind: selection.kind,
          match_nothing: true,
        });
      }
      continue;
    }
    if (selection.kind === "asOf") {
      requests.push({
        by: selection.by,
        value: selection.dimValue,
        kind: "asOf",
        start_date: selection.asOfDate,
        end_date: selection.asOfDate,
      });
      continue;
    }
    if (selection.kind === "dateRange") {
      // A preset travels as the preset. Resolving it here instead would pin the widgets to this
      // browser's idea of "last week", and to the moment the request happened to be built.
      requests.push(
        selection.datePreset !== null
          ? {
              by: selection.by,
              value: selection.dimValue,
              kind: "dateRange",
              date_preset: selection.datePreset,
            }
          : {
              by: selection.by,
              value: selection.dimValue,
              kind: "dateRange",
              start_date: selection.startDate,
              end_date: selection.endDate,
            },
      );
    } else {
      requests.push({
        by: selection.by,
        value: selection.dimValue,
        kind: selection.kind,
        values: selection.values,
        ...(selection.rawValues ? { raw_values: true } : {}),
      });
    }
  }
  return requests;
}

/** Whether a filter takes several values. A list of named periods applies one window. */
export function isMultiSelect(dimension: TabFilterDimension): boolean {
  if (dimension.filterKind === "dateRange" || dimension.filterKind === "asOf") {
    return false;
  }
  return dimension.selectionConfig?.multi ?? true;
}

/** The names a filter can be referred to by in another filter's placeholder, normalised. */
function referenceNames(dimension: TabFilterDimension): string[] {
  return [dimension.value, dimension.name, dimension.defaultName ?? ""]
    .map((name) => name.trim().toLowerCase())
    .filter((name) => name !== "");
}

/**
 * The filters whose values query reads any of `changed` (dimension values), directly or through
 * another filter — so a change to Title re-runs Seasons, and anything that reads Seasons too.
 *
 * Filters on the levels of one multi-column ontology depend on each other both ways (a category
 * narrows its sub categories and a sub category narrows its categories), so a change to any of
 * them brings in all the others.
 */
function sharesMovedOntology(
  dimension: TabFilterDimension,
  dimensions: TabFilterDimension[],
  moved: ReadonlySet<string>,
): boolean {
  const ontologyKey = dimension.ontologyLevel?.ontologyKey;
  if (!ontologyKey) {
    return false;
  }
  return dimensions.some(
    (other) => moved.has(other.value) && other.ontologyLevel?.ontologyKey === ontologyKey,
  );
}

export function dependentDimensions(
  changed: string[],
  dimensions: TabFilterDimension[],
): TabFilterDimension[] {
  const moved = new Set(changed);
  const dependents: TabFilterDimension[] = [];
  let grew = true;
  while (grew) {
    grew = false;
    const movedNames = new Set(
      dimensions
        .filter((dimension) => moved.has(dimension.value))
        .flatMap((dimension) => referenceNames(dimension)),
    );
    for (const dimension of dimensions) {
      if (moved.has(dimension.value)) {
        continue;
      }
      const reads = (dimension.parents ?? []).some((parent) =>
        movedNames.has(parent.trim().toLowerCase()),
      );
      if (reads || sharesMovedOntology(dimension, dimensions, moved)) {
        moved.add(dimension.value);
        dependents.push(dimension);
        grew = true;
      }
    }
  }
  return dependents;
}

/**
 * A tab's request with the app-level filters brought up to date and the rest left as applied.
 *
 * For a tab opened after an app-level filter was applied on another one: the app-level picks
 * (`appLevelDims`) are taken from `selections`, which already carry them over, while the tab's own
 * filters keep what was last applied to it (`applied`) — an unapplied edit of its own stays
 * unapplied. A tab never applied this session (`applied` undefined) uses its selection throughout.
 * Kept in selection order, so it compares equal to the tab's selection once nothing is pending.
 */
export function withAppLevelFilters(
  selections: TabFilterSelection[],
  appLevelDims: ReadonlySet<string>,
  applied: AppTabFilterRequest[] | undefined,
): AppTabFilterRequest[] {
  const appliedByDim = new Map((applied ?? []).map((request) => [request.value, request]));
  return selections.flatMap((selection) => {
    if (applied === undefined || appLevelDims.has(selection.dimValue)) {
      return toTabFilterRequest([selection]);
    }
    const own = appliedByDim.get(selection.dimValue);
    return own === undefined ? [] : [own];
  });
}

/**
 * The filters `dimension`'s values query reads, directly or through another filter — what has to
 * come along when it is applied to all tabs, since every tab must then have its parents too.
 */
export function ancestorDimensions(
  dimension: TabFilterDimension,
  dimensions: TabFilterDimension[],
): TabFilterDimension[] {
  const ancestors: TabFilterDimension[] = [];
  const seen = new Set([dimension.value]);
  const pending = [dimension];
  while (pending.length > 0) {
    const current = pending.pop() as TabFilterDimension;
    const reads = new Set((current.parents ?? []).map((parent) => parent.trim().toLowerCase()));
    for (const candidate of dimensions) {
      if (seen.has(candidate.value)) {
        continue;
      }
      if (referenceNames(candidate).some((name) => reads.has(name))) {
        seen.add(candidate.value);
        ancestors.push(candidate);
        pending.push(candidate);
      }
    }
  }
  return ancestors;
}

/** The filters (by dimension value) whose selection differs between two selection lists. */
export function changedSelections(
  previous: TabFilterSelection[],
  next: TabFilterSelection[],
): string[] {
  const before = new Map(previous.map((selection) => [selection.dimValue, JSON.stringify(selection)]));
  return next
    .filter((selection) => before.get(selection.dimValue) !== JSON.stringify(selection))
    .map((selection) => selection.dimValue);
}

/** Each selection as the value payload the server takes, keyed by dimension value. */
export function selectionsToPayload(
  selections: TabFilterSelection[],
): Record<string, TabFilterValue> {
  return Object.fromEntries(
    selections.map((selection) => [selection.dimValue, selectionToValuePayload(selection)]),
  );
}

/**
 * The placeholder another filter's values query uses to read `filterValue`'s selection — the
 * author swaps `column` for the column it should match. See the backend's
 * `tab_filter_dependencies.py` for what it becomes when run.
 */
export function filterPlaceholder(filterValue: string): string {
  return `{{${filterValue}: column}}`;
}

/**
 * `selection` with the filter `movedDim` dropped where `targetDim` stands, or `null` when the
 * move is not allowed. App-level filters (`appLevelDims`) always lead the bar, so a filter only
 * moves within its own group — an app-level one among app-level ones, a tab's own among the tab's.
 */
export function moveFilter(
  selection: TabFilterSelection[],
  movedDim: string,
  targetDim: string,
  appLevelDims: ReadonlySet<string>,
): TabFilterSelection[] | null {
  const from = selection.findIndex((item) => item.dimValue === movedDim);
  const to = selection.findIndex((item) => item.dimValue === targetDim);
  if (from === -1 || to === -1 || from === to) {
    return null;
  }
  if (appLevelDims.has(movedDim) !== appLevelDims.has(targetDim)) {
    return null;
  }
  const next = [...selection];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}
